import { useState, useCallback, useEffect, useRef } from "react";
import { useSWR } from "./useSWR.ts";
import { useToast } from "./ToastContext.tsx";
import { formatTime } from "./utils.ts";
import { classifyClass } from "./classCategory.ts";

interface Participant {
  customerId: string;
  name: string;
  arrived: boolean;
}

interface ClassData {
  id: string;
  productname: string;
  start: string; // "2026-02-10 16:00:00"
  end: string;
  employee: string[];
  customer: string[];
  participant_infos: Record<string, { name: string; has_arrived: boolean }>;
  maxparticipants: number;
}

function formatDate(dateStr: string): string {
  // "2026-02-10 16:00:00" → local Date
  const d = new Date(dateStr.replace(" ", "T"));
  return d.toLocaleDateString("fi-FI", { weekday: "short", day: "numeric", month: "short" });
}

function parseParticipants(data: ClassData): Participant[] {
  const customers: string[] = data.customer || [];
  const infos = data.participant_infos || {};
  const list: Participant[] = customers.map((cid) => {
    const info = infos[cid];
    return {
      customerId: cid,
      name: info?.name || cid,
      arrived: Boolean(info?.has_arrived),
    };
  });
  list.sort((a, b) => {
    if (a.arrived !== b.arrived) return a.arrived ? 1 : -1;
    return a.name.localeCompare(b.name, "fi");
  });
  return list;
}

async function fetchClass(key: string): Promise<ClassData> {
  const id = key.replace("class/", "");
  const r = await fetch(`/api/classes/${id}`);
  if (r.status === 401) throw new Error("__401__");
  const data = await r.json();
  if (data?.error) throw new Error(data.error);
  return data;
}

export function ClassDetail({
  classId,
  onBack,
  onSessionExpired,
  exiting = false,
  skipAnimation = false,
}: {
  classId: string;
  onBack: () => void;
  onSessionExpired: () => void;
  exiting?: boolean;
  skipAnimation?: boolean;
}) {
  const { data: classData, error, isLoading, mutate } = useSWR<ClassData>(
    `class/${classId}`,
    fetchClass,
    { refreshInterval: 60_000 },
  );
  const [toggling, setToggling] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, boolean>>({});
  const [entered, setEntered] = useState(false);
  const { addToast } = useToast();
  const frameRef = useRef(0);

  useEffect(() => {
    frameRef.current = requestAnimationFrame(() => {
      setEntered(true);
    });
    return () => cancelAnimationFrame(frameRef.current);
  }, []);

  if (error === "__401__") { onSessionExpired(); return null; }

  const toggleArrived = useCallback(async (p: Participant) => {
    setToggling(p.customerId);
    const newArrived = !p.arrived;
    // Optimistic update
    setLocalOverrides((prev) => ({ ...prev, [p.customerId]: newArrived }));
    try {
      const res = await fetch(`/api/classes/${classId}/attendance`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ customerId: p.customerId, arrived: newArrived }),
      });
      if (!res.ok) {
        // Revert on failure
        setLocalOverrides((prev) => {
          const next = { ...prev };
          delete next[p.customerId];
          return next;
        });
        addToast("Läsnäolotieto ei päivittynyt", "error");
      }
    } catch {
      setLocalOverrides((prev) => {
        const next = { ...prev };
        delete next[p.customerId];
        return next;
      });
      addToast("Läsnäolotieto ei päivittynyt", "error");
    } finally {
      setToggling(null);
      // Revalidate from server after toggle
      mutate();
    }
  }, [classId, mutate, addToast]);

  const animClass = skipAnimation ? "detail-entered" : exiting ? "detail-exiting" : entered ? "detail-entered" : "detail-entering";

  if (error) return <div className={`class-detail ${animClass}`}><div className="loading">{error}</div></div>;
  if (isLoading || !classData) return <div className={`class-detail ${animClass}`}><div className="loading"><div className="spinner" /></div></div>;

  const participants = parseParticipants(classData).map((p) => ({
    ...p,
    arrived: localOverrides[p.customerId] ?? p.arrived,
  }));
  const arrivedCount = participants.filter((p) => p.arrived).length;

  return (
    <div className={`class-detail detail-loaded ${animClass}`}>
      <button className="back-button" onClick={onBack}>
        ← Takaisin
      </button>
      <div className="detail-header" style={{ borderLeft: `4px solid var(--cat-${classifyClass(classData.productname)})` }}>
        <h2>{classData.productname?.trim()}</h2>
        <div className="meta">
          {formatDate(classData.start)} {formatTime(classData.start)} – {formatTime(classData.end)}
        </div>
        <div className="meta">
          Paikalla: {arrivedCount} / {participants.length}
          {classData.maxparticipants
            ? ` (max ${classData.maxparticipants})`
            : ""}
        </div>
      </div>
      <div className="participant-list">
        <h3>Osallistujat ({participants.length})</h3>
        {participants.length === 0 && (
          <div className="empty">Ei osallistujia</div>
        )}
        {participants.map((p) => (
          <div key={p.customerId} className="participant">
            <span className="name">{p.name}</span>
            <button
              className={`arrive-btn ${p.arrived ? "arrived" : "not-arrived"}`}
              onClick={() => toggleArrived(p)}
              disabled={toggling === p.customerId}
            >
              {p.arrived ? "Paikalla" : "Merkitse"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
